import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { EmptyState } from '../../../../components/common/empty/EmptyState';
import { BrandFilterToggleSection } from '../../../../components/common/filter/BrandFilterToggleSection';
import { FilterSection } from '../../../../components/common/filter/FilterSection/FilterSection';
import { FilterSlideModal } from '../../../../components/common/filter/FilterSlideModal/FilterSlideModal';
import { MainHead } from '../../../../components/common/head/MainHead';
import { AnimateLoading } from '../../../../components/common/loading/AnimateLoading';
import { IDirectDealFilter } from '../../../../constants/filter/directDealFilter.constant';
import { pageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { useGetNewOpenMarketProductQuery } from '../../../../hooks/api/product/queries/useGetNewOpenMarketProduct.query';
import { useInfiniteOpenMarketProducts } from '../../../../hooks/api/product/queries/useInfiniteOpenMarketProducts';
import { useAddFilterOptions } from '../../../../hooks/filter/useAddFilterOptions';
import { useCheckSearchParamUpdate } from '../../../../hooks/filter/useCheckSearchParamUpdate';
import { useUpdateDirectDealFilterStates } from '../../../../hooks/filter/useUpdateDirectDealFilterStates';
import { useUpdateDirectDealListPageSearchParam } from '../../../../hooks/filter/useUpdateDirectDealListPageSearchParam';
import { useGetDownloadAppModalHeight } from '../../../../hooks/modal/useGetDownloadAppModalHeight';
import { useMarkNotificationAsRead } from '../../../../hooks/page/useMarkNotificationAsRead';
import { useLayout } from '../../../../hooks/screen/useLayout';
import { useInfiniteScrollLoadingTarget } from '../../../../hooks/scroll/useInfiniteScrollLoadingTarget';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store/useStore';
import { useGetUserProfile } from '../../../../hooks/user/useGetUserProfile';
import { filterSlice } from '../../../../store/filterSlice';
import { createDirectDealFilterSearchParams } from '../../../../utils/filter/createDirectDealFilterSearchParams.util';
import {
  IOpenMarketProductForDetailCard,
  OpenMarketProductDetailCard,
} from '../../../../views/Product/listView/OpenMarketProductDetailCard/OpenMarketProductDetailCard';
import { OpenMarketProductListDetailCardGroup } from '../../../../views/Product/listView/OpenMarketProductDetailCard/OpenMarketProductListDetailCardGroup';
import { MarketAlarmButton } from '../../../Product/ProductListPage/components/MarketAlarmButton';
import { OpenMarketCarouselBanners } from '../../components/OpenMarketCarouselBanners';
import { OpenMarketListWrapper } from '../../components/OpenMarketListWrapper';
import { SellTractorButton } from '../../components/SellTractorButton';

export const DirectDealListPage = () => {
  const dispatch = useAppDispatch();
  const filterActions = filterSlice.actions;
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const { isGetBrandsQueryLoading } = useAddFilterOptions({
    filterType: 'directDealFilter',
    data: 'brands',
    applicableMachineType: 'ALL',
  });
  useCheckSearchParamUpdate({ filterType: 'directDealFilter' });
  useUpdateDirectDealFilterStates();
  useUpdateDirectDealListPageSearchParam();

  const { userProfile } = useGetUserProfile();
  const directDealFilterStates = useAppSelector((state) => state.filter.directDealFilter);
  useEffect(() => {
    if (directDealFilterStates.globalFilters.length > 0 && !directDealFilterStates.selectedFilterKey) {
      const hasNonDefaultFilters = directDealFilterStates.globalFilters.some((filter) => {
        if (filter.optionType === 'radio' || filter.optionType === 'checkbox') {
          return filter.options.some((option) => option.isSelected && !option.isDefault);
        }
        if (filter.optionType === 'slider') {
          return filter.options.some((option) => option.value !== option.defaultValue);
        }
        return false;
      });

      if (!hasNonDefaultFilters) {
        dispatch(
          filterActions.setSelectedFilterKey({
            filterType: 'directDealFilter',
            value: 'filter_brand',
          }),
        );
        dispatch(
          filterActions.setIsAutoOpened({
            filterType: 'directDealFilter',
            value: true,
          }),
        );
      }
    }
  }, [directDealFilterStates.globalFilters, directDealFilterStates.selectedFilterKey, dispatch, filterActions]);

  const { data: newOpenMarketProduct } = useGetNewOpenMarketProductQuery();
  useMarkNotificationAsRead(newOpenMarketProduct, 'newOpenMarketProductInfo');

  const filterSearchParams = createDirectDealFilterSearchParams({
    filters: directDealFilterStates.globalFilters as IDirectDealFilter[],
  });

  const {
    data: products,
    isLoading: isGetProductsQueryLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteOpenMarketProducts({
    searchParam: {
      ...filterSearchParams,
      count: 4,
    },
  });

  const { loadingTargetRef, isLoadingTargetRendered } = useInfiniteScrollLoadingTarget({
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    triggeredFnWhenInView: fetchNextPage,
  });

  const fixedHeaderHeight = userProfile?.advertisementTermsAgreement ? 48 : 86;
  const downloadAppModalHeight = useGetDownloadAppModalHeight();

  const handleFilterButtonClick = () => {
    setIsFilterModalOpen(true);
    window.scrollTo(0, 0);
  };

  useLayout({
    fixedHeader: (
      <>
        <MainHead backButton={{ isShow: false }} headerTitle={'회원 직거래'} hasMyInfoButton />
        <MarketAlarmButton pageType={'OLP'} />
      </>
    ),
    fixedHeaderHeight,
    fixedFooter: <SellTractorButton pageType={'olp'} />,
    fixedFooterHeight: 0,
    style: { background: '#fff' },
  });

  if (isGetBrandsQueryLoading) {
    return <AnimateLoading />;
  }

  return (
    <>
      <Helmet>
        <title>{pageInfo.openMarketListPage.title}</title>
      </Helmet>

      <OpenMarketCarouselBanners category={'CAROUSEL_MAIN'} outputLocation={'open-market'} />

      <BrandFilterToggleSection filterType={'directDealFilter'} />

      <FilterSection
        filterType={'directDealFilter'}
        style={{
          position: 'sticky',
          top: `${fixedHeaderHeight + downloadAppModalHeight}px `,
          backgroundColor: 'white',
          zIndex: 55,
        }}
      />

      <OpenMarketListWrapper>
        <ProductListContainer>
          <OpenMarketProductListDetailCardGroup>
            {products &&
              products.pages.map((page, pageIndex) => {
                return page.map((product) => {
                  return (
                    <OpenMarketProductDetailCard
                      key={product.id}
                      product={product as IOpenMarketProductForDetailCard}
                      isNewBadgeVisible
                      indexInList={pageIndex * 4 + page.indexOf(product)}
                    />
                  );
                });
              })}
            {!products?.pages?.[0]?.length && (
              <EmptyState
                title={'찾으시는 결과가 없네요.'}
                buttonText={'다시 시도하기'}
                onClick={handleFilterButtonClick}
              />
            )}
            {isLoadingTargetRendered && <LoadingTarget ref={loadingTargetRef} />}
          </OpenMarketProductListDetailCardGroup>
        </ProductListContainer>
      </OpenMarketListWrapper>

      {isFilterModalOpen && (
        <FilterSlideModal
          isShow={isFilterModalOpen}
          filterType={'directDealFilter'}
          closeModal={() => setIsFilterModalOpen(false)}
        />
      )}
    </>
  );
};

const ProductListContainer = styled.div`
  display: flex;
  padding: 8px 16px 32px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const LoadingTarget = styled.div``;
